import { Injectable } from '@angular/core';
import { Experiences, Permissions } from '@local/client-contracts';
import { observable } from '@local/common';
import { LogService, ServicesRpcService } from '@shared/services';
import { DeepPartial } from '@local/ts-infra';

import { Logger } from '@unleash-tech/js-logger';
import { Observable, ReplaySubject, firstValueFrom, map } from 'rxjs';

import { ExperiencesRpcInvoker } from './invokers/experiences-rpc-invoker';
import { ExperienceItem } from '@local/client-contracts/src/experiences';
import { SessionService } from '@shared/services/session.service';

@Injectable({
  providedIn: 'root',
})
export class ExperiencesInvokerService {
  private logger: Logger;
  private service: Experiences.Service;
  private _all$ = new ReplaySubject<Experiences.ExperienceItem[]>(1);
  private _defaultAssistant$ = new ReplaySubject<Experiences.ExperienceItem>(1);
  defaultAssistant: Experiences.ExperienceItem<'general'> | null = null;

  private _allExperiences: Experiences.ExperienceItem[] = [];

  @observable
  get all$(): Observable<Experiences.ExperienceItem[]> {
    return this._all$;
  }

  get allExperiences(): Experiences.ExperienceItem[] {
    return this._allExperiences;
  }

  @observable
  get visible$(): Observable<Experiences.ExperienceItem[]> {
    return this.all$.pipe(map((all) => all?.filter((e) => e?.effectiveScopes?.find((ef) => ['write', 'read'].includes(ef?.scope)))));
  }

  @observable
  get defaultAssistant$(): Observable<Experiences.ExperienceItem> {
    return this._defaultAssistant$;
  }

  constructor(
    services: ServicesRpcService,
    logger: LogService,
    private sessionService: SessionService
  ) {
    this.logger = logger.scope('AssistantInvokerService');
    this.service = services.invokeWith(ExperiencesRpcInvoker, 'experiences');

    this.service.all$.subscribe(async (all: ExperienceItem[]) => {
      const active = await this.setDefaultAssistant(all);
      this._allExperiences = active;
      this._all$.next(active);
    });
  }

  create(
    type: Experiences.ExperienceType,
    body?: Experiences.ExperienceItemData,
    unavailableFilterSourceIds?: number[]
  ): Promise<Experiences.ExperienceItem> {
    return this.service.create(type, body, unavailableFilterSourceIds);
  }

  delete(id: string): Promise<void> {
    return this.service.delete(id);
  }

  update(
    assistant: Experiences.ExperienceItem,
    unavailableFilterSourceIds?: number[],
    ignoreUpdateDataSources?: boolean,
    toolIds?: string[]
  ): Promise<Experiences.ExperienceUpdateResponse> {
    return this.service.updateById(assistant, unavailableFilterSourceIds, ignoreUpdateDataSources, toolIds);
  }

  updateShareOptions(id: string, shareOptions: Permissions.ShareOptions, type: Experiences.ExperienceType): Promise<void> {
    return this.service.updateShareOptions(id, shareOptions, type);
  }

  getExperience$(id: string, cache?: Experiences.CacheType) {
    return this.service.getExperience$(id, cache);
  }

  getSharedObjects(accountId: string): Promise<Experiences.ExperienceSharedObjects> {
    return this.service.getSharedObjects(accountId);
  }

  refresh(): Promise<void> {
    return firstValueFrom(this.service.refresh$());
  }

  updatePartialSettingsById(id: string, updatedData: DeepPartial<Experiences.AnyExperienceSettings>) {
    return this.service.updatePartialSettingsById(id, updatedData);
  }

  updatePartialDraftSettingsById(
    assistant: Experiences.ExperienceItem,
    updatedDataSettings: DeepPartial<Experiences.AnyExperienceSettings>
  ) {
    return this.service.updatePartialDraftSettingsById(assistant, updatedDataSettings);
  }

  private async setDefaultAssistant(all: ExperienceItem[]): Promise<ExperienceItem[]> {
    const session = await firstValueFrom(this.sessionService.current$);
    const defaultAssistantId = session?.workspace?.settings?.assistant?.searchExperienceId;
    const defaultAssistant = all?.find((a) => a?.id === defaultAssistantId);
    const settings = defaultAssistant?.settings as Experiences.ExperienceGeneralSettings;
    if (settings) {
      settings.general.pinToChatPage = settings?.general?.pinToChatPage ?? true;
    } else if (defaultAssistant) {
      defaultAssistant.settings = { general: { pinToChatPage: true } } as Experiences.ExperienceGeneralSettings;
    }
    this._defaultAssistant$.next(defaultAssistant || null);
    this.defaultAssistant = (defaultAssistant as Experiences.ExperienceItem<'general'>) || null;
    return all?.filter((a) => a?.id !== defaultAssistantId);
  }

  @observable
  getSlackExperts$(slackDefaultLinkId: string, conversationId: string): Observable<Experiences.SlackExpertResponse> {
    return this.service.getSlackExperts$(slackDefaultLinkId, conversationId);
  }
}
